import * as THREE from 'three';

export type WeatherKind = 'clear' | 'rain' | 'snow';

const RAIN_COUNT = 2200;
const SNOW_COUNT = 1400;
const BOX = 26; // particles live in a box this size around the camera
const HEIGHT = 14;

/**
 * Rain and snow around the camera. Particle counts are fixed per kind and no
 * lights are involved, so switching weather never forces shader recompiles.
 * `shelter` (0..1) fades it out under cover (e.g. an arcade roof).
 */
export class Weather {
  kind: WeatherKind = 'clear';
  private readonly group = new THREE.Group();
  private readonly rain: THREE.LineSegments;
  private readonly snow: THREE.Points;
  private readonly rainPos: Float32Array;
  private readonly snowPos: Float32Array;
  private readonly snowDrift: Float32Array;
  private shelter = 0;
  private time = 0;

  constructor(scene: THREE.Scene) {
    // Rain: short streaks (two vertices each).
    this.rainPos = new Float32Array(RAIN_COUNT * 6);
    for (let i = 0; i < RAIN_COUNT; i += 1) this.respawnRain(i, Math.random() * HEIGHT, 0, 0);
    const rainGeo = new THREE.BufferGeometry();
    rainGeo.setAttribute('position', new THREE.BufferAttribute(this.rainPos, 3));
    const rainMat = new THREE.LineBasicMaterial({ color: '#9fb2cc', transparent: true, opacity: 0.32, depthWrite: false });
    this.rain = new THREE.LineSegments(rainGeo, rainMat);
    this.rain.frustumCulled = false;

    // Snow: soft points that drift.
    this.snowPos = new Float32Array(SNOW_COUNT * 3);
    this.snowDrift = new Float32Array(SNOW_COUNT);
    for (let i = 0; i < SNOW_COUNT; i += 1) {
      this.snowPos[i * 3] = (Math.random() - 0.5) * BOX;
      this.snowPos[i * 3 + 1] = Math.random() * HEIGHT;
      this.snowPos[i * 3 + 2] = (Math.random() - 0.5) * BOX;
      this.snowDrift[i] = Math.random() * Math.PI * 2;
    }
    const snowGeo = new THREE.BufferGeometry();
    snowGeo.setAttribute('position', new THREE.BufferAttribute(this.snowPos, 3));
    const snowMat = new THREE.PointsMaterial({ color: '#e8eef7', size: 0.07, transparent: true, opacity: 0.85, depthWrite: false });
    this.snow = new THREE.Points(snowGeo, snowMat);
    this.snow.frustumCulled = false;

    this.group.add(this.rain, this.snow);
    this.rain.visible = false;
    this.snow.visible = false;
    scene.add(this.group);
  }

  setKind(kind: WeatherKind): void {
    this.kind = kind;
    this.rain.visible = kind === 'rain';
    this.snow.visible = kind === 'snow';
  }

  /** 0..1 how loud the rain is where she stands (for audio and his footsteps). */
  get rainLevel(): number {
    return this.kind === 'rain' ? 1 - this.shelter * 0.85 : 0;
  }

  update(dt: number, center: THREE.Vector3, sheltered: boolean): void {
    this.time += dt;
    this.shelter += ((sheltered ? 1 : 0) - this.shelter) * Math.min(1, dt * 3);
    this.group.position.set(center.x, 0, center.z);
    if (this.kind === 'rain') {
      (this.rain.material as THREE.LineBasicMaterial).opacity = 0.32 * (1 - this.shelter);
      const fall = 17 * dt;
      for (let i = 0; i < RAIN_COUNT; i += 1) {
        const o = i * 6;
        this.rainPos[o + 1] -= fall;
        this.rainPos[o + 4] -= fall;
        if (this.rainPos[o + 4] < 0) this.respawnRain(i, HEIGHT, 0, 0);
      }
      this.rain.geometry.attributes.position.needsUpdate = true;
    } else if (this.kind === 'snow') {
      (this.snow.material as THREE.PointsMaterial).opacity = 0.85 * (1 - this.shelter * 0.9);
      for (let i = 0; i < SNOW_COUNT; i += 1) {
        const o = i * 3;
        this.snowPos[o + 1] -= 1.1 * dt;
        this.snowPos[o] += Math.sin(this.time * 0.7 + this.snowDrift[i]) * 0.35 * dt;
        if (this.snowPos[o + 1] < 0) {
          this.snowPos[o + 1] = HEIGHT;
          this.snowPos[o] = (Math.random() - 0.5) * BOX;
          this.snowPos[o + 2] = (Math.random() - 0.5) * BOX;
        }
      }
      this.snow.geometry.attributes.position.needsUpdate = true;
    }
  }

  private respawnRain(i: number, y: number, _cx: number, _cz: number): void {
    const x = (Math.random() - 0.5) * BOX;
    const z = (Math.random() - 0.5) * BOX;
    const o = i * 6;
    this.rainPos[o] = x;
    this.rainPos[o + 1] = y;
    this.rainPos[o + 2] = z;
    this.rainPos[o + 3] = x + 0.05;
    this.rainPos[o + 4] = y - 0.55;
    this.rainPos[o + 5] = z + 0.02;
  }

  dispose(): void {
    this.rain.geometry.dispose();
    (this.rain.material as THREE.Material).dispose();
    this.snow.geometry.dispose();
    (this.snow.material as THREE.Material).dispose();
    this.group.removeFromParent();
  }
}
